import React, { useEffect } from 'react';
import {
  FormControl,
  makeStyles,
  Select,
  Theme,
  FormHelperText,
} from '@material-ui/core';
import { ReactNode } from 'react';
import { useRef } from 'react';
import { useState } from 'react';
import Typo from './Typo';
import colorPalette from '../themes/colorPalette';

const { grey, primary, white, red } = colorPalette;

type CustomSelectProps = {
  children: ReactNode;
  onChange:
    | ((
        event: React.ChangeEvent<{
          name?: string | undefined;
          value: unknown;
        }>,
        child: React.ReactNode
      ) => void)
    | undefined;
  value: string | unknown;
  fullWidth?: boolean;
  label?: string;
  name?: string;
  errorMsg?: string | false;
  disabled?: boolean;
  round?: boolean;
  size?: 'large' | 'small';
  className?: string;
  backgroundColor?: string;
  borderColor?: string;
  onFocus?:
    | React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  onBlur?:
    | React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  [key: string]: any;
};

type StylesProps = {
  isOpen: boolean;
  isError: boolean;
  isDisabled: boolean;
  isRound: boolean;
  size: 'large' | 'small';
  isLabel: boolean;
  backgroundColor: string;
  borderColor: string;
};

const useStyles = makeStyles<Theme, StylesProps>({
  root: {
    transition: '200ms',
    boxShadow: 'inset 0 0 0 0 rgba(0,0,0,0), 0 0 0 0 rgba(0,0,0,0)',
    '&:focus': {},
  },
  select: {
    fontSize: '17px',
    color: ({ isDisabled }) => (isDisabled ? grey[8] : grey[10]),
    letterSpacing: '0.1px',
    border: ({ isError, isDisabled, borderColor }) => {
      if (isError) return `solid 1px ${red[5]}`;
      return `solid 1px ${isDisabled ? grey[3] : borderColor}`;
    },
    padding: ({ isLabel, size }) => {
      if (isLabel)
        return size === 'large' ? '24px 24px 8px 20px' : '16px 24px 4px 20px';
      return size === 'large' ? '16px 24px 16px 20px' : '9px 24px 9px 20px';
    },
    borderRadius: ({ isRound }) => getBorderRadius(isRound),
    backgroundColor: ({ isDisabled, isOpen, isError, backgroundColor }) => {
      if (isDisabled) return grey[3];
      if (isOpen || isError) return white;
      return backgroundColor;
    },
    transition: '200ms',
    '&:hover': {
      backgroundColor: ({ isDisabled }) => (isDisabled ? grey[3] : grey[1]),
    },
    '&:focus': {
      borderRadius: ({ isRound }) => getBorderRadius(isRound),
      backgroundColor: white,
    },
  },
  selectCustom: {
    marginTop: 0 + ' !important',
    textAlign: 'left',
    '& > div': {
      boxShadow: `inset 0 0 0 1px rgba(242, 242, 244, 0), 0 0 0 3px rgba(242, 242, 244, 0)`,
    },
    '&:focus': {},
  },
  selectMenu: {
    overflow: 'visible',
  },
  selectIcon: {
    marginRight: '8px',
  },
  selectCustomOpen: {
    '& > div': {
      boxShadow: `inset 0 0 0 1px ${primary.main}, 0 0 0 3px rgba(85, 105, 221, 0.4) !important`,
    },
  },
  label: ({ isError, isDisabled }) => ({
    position: 'absolute',
    top: '50%',
    left: '20px',
    transform: 'translateY(-50%)',
    transition: '200ms',
    zIndex: 1,
    cursor: isDisabled ? 'auto' : 'pointer',
    '&:hover': {
      '& + div > div': {
        backgroundColor: isDisabled ? grey[3] : grey[1],
      },
    },
    '& > p': {
      fontSize: '17px',
      transition: '200ms',
      color: () => {
        if (isDisabled) return grey[8];
        return isError ? red[5] : grey[8];
      },
    },
  }),
  labelTop: {
    top: '25% !important',
    '& > p': {
      fontSize: '12px !important',
    },
  },
  labelActive: ({ isError }) => {
    return {
      '& > p': {
        color: `${isError ? red[5] : primary.main} !important`,
      },
    };
  },
  container: {
    width: '100%',
  },
  menu: {
    marginTop: '4px',
  },
  inputContainer: {
    position: 'relative',
  },
  errorMsg: {
    marginLeft: '20px',
    '&.MuiFormHelperText-root.Mui-error': {
      color: red[5],
    },
  },
  selectCustomOpenError: {
    '& > div': {
      boxShadow:
        'inset 0 0 0 1px rgba(203, 80, 76, 0.5), 0 0 0 3px rgba(203, 80, 76, 0.5) !important',
    },
  },
});

function CustomSelect({
  children,
  fullWidth = true,
  label,
  onChange,
  value,
  name,
  errorMsg,
  disabled,
  round,
  size = 'large',
  className,
  backgroundColor = grey[2],
  borderColor = grey[5],
  onFocus,
  onBlur,
  ...other
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);

  const classes = useStyles({
    isOpen,
    isError: !!errorMsg,
    isDisabled: !!disabled,
    isRound: !!round,
    size,
    isLabel: !!label,
    backgroundColor,
    borderColor,
  });

  const [labelClasses, setLabelClasses] = useState<
    ('labelTop' | 'labelActive')[]
  >(value ? ['labelTop'] : []);
  const [selectClasses, setSelectClasses] = useState<
    ('selectCustomOpen' | 'selectCustomOpenError')[]
  >([]);
  const selectRef = useRef<any>(null);

  const labelClassesMapped = labelClasses.map(c => classes[c]).join(' ');
  const selectClassesMapped = selectClasses.map(c => classes[c]).join(' ');

  const onFocusSelect = (
    ev: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onFocus && onFocus(ev);
    setLabelClasses(['labelTop', 'labelActive']);
    if (errorMsg) setSelectClasses(['selectCustomOpenError']);
    else setSelectClasses(['selectCustomOpen']);
  };

  const onBlurSelect = (
    ev: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onBlur && onBlur(ev);
    if (value) {
      setLabelClasses(['labelTop']);
      setSelectClasses([]);
    } else {
      setLabelClasses([]);
      setSelectClasses([]);
    }
  };

  useEffect(() => {
    if (
      (selectRef.current && document.activeElement === selectRef.current) ||
      document.activeElement === selectRef.current.firstElementChild
    ) {
      if (errorMsg) setSelectClasses(['selectCustomOpenError']);
      else setSelectClasses(['selectCustomOpen']);
    }
  }, [errorMsg]);

  const onClose = () => {
    setIsOpen(false);
    selectRef.current && selectRef.current.blur();
  };

  return (
    <FormControl className={classes.container}>
      <div className={classes.inputContainer}>
        {label && (
          <label
            id={label}
            className={`${classes.label} ${labelClassesMapped}`}
            onClick={() => !disabled && setIsOpen(true)}
            htmlFor={name}
          >
            <Typo variant="bodyMd" color="muted">
              {label}
            </Typo>
          </label>
        )}
        <Select
          labelId={label}
          label={label}
          onChange={onChange}
          value={value}
          name={name}
          id={name}
          fullWidth={fullWidth}
          innerRef={selectRef}
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={onClose}
          onFocus={onFocusSelect}
          onBlur={onBlurSelect}
          MenuProps={{
            className: classes.menu,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'center',
            },
            getContentAnchorEl: null,
          }}
          className={`${
            classes.selectCustom
          } ${selectClassesMapped} ${className || ''}`}
          classes={{
            root: classes.root,
            select: classes.select,
            icon: classes.selectIcon,
            selectMenu: classes.selectMenu,
          }}
          disableUnderline
          disabled={disabled}
          {...other}
        >
          {children}
        </Select>
      </div>
      {errorMsg && (
        <FormHelperText error className={classes.errorMsg}>
          {errorMsg}
        </FormHelperText>
      )}
    </FormControl>
  );
}

function getBorderRadius(isRound: boolean) {
  return isRound ? '27px' : '16px';
}

export default CustomSelect;
